'use client';

import type { FC, ReactNode, WheelEvent } from 'react';
import { DesktopHeader } from './DesktopHeader';
import { DesktopNav } from './DesktopNav';
import { DesktopDropsFeed } from './DesktopDropsFeed';

interface DesktopLayoutProps {
  children: ReactNode;
  minimal?: boolean;
}

export const DesktopLayout: FC<DesktopLayoutProps> = ({ children, minimal = false }) => {
  // Content is constrained to a 4xl-wide column. The wider viewport
  // leaves dead space on either side that isn't a scroll container,
  // so the wheel does nothing when the cursor sits there. Forward
  // wheel events from the empty side area into the screen's actual
  // scroll container (ScreenContent's div, marked with the
  // `data-screen-scroll` attribute).
  const handleSideWheel = (e: WheelEvent<HTMLElement>) => {
    const scroller = e.currentTarget.querySelector(
      '[data-screen-scroll]',
    ) as HTMLElement | null;
    if (!scroller) return;
    if (scroller.contains(e.target as Node)) return;
    scroller.scrollBy({ top: e.deltaY, left: e.deltaX, behavior: 'auto' });
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden pt-[calc(var(--tg-viewport-safe-area-inset-top,0px)+var(--tg-viewport-content-safe-area-inset-top,0px))]" style={{ background: 'linear-gradient(160deg, #07090D 0%, #0A0E18 50%, #07090D 100%)' }}>
      {!minimal && <DesktopDropsFeed />}
      {!minimal && <DesktopHeader />}
      {!minimal && <DesktopNav />}
      <div className="flex flex-1 min-h-0">
        <main
          className="flex-1 min-w-0 relative outline-none focus:outline-none focus-visible:outline-none"
          onWheel={handleSideWheel}
        >
          <div className="w-full max-w-4xl mx-auto h-full relative">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
